// Mirror of the server's analysis gate (crates/tres-api/src/analysis_gate.rs):
// one whole-store analysis runs at a time, and a surface that waits for one
// says which analysis, who started it, and when.

import { describeApiError } from './apiErrors';

export type Analysis = 'data_review' | 'readiness' | 'namespace_usage';

export interface AnalysisRun {
  analysis: Analysis;
  started_by: string;
  started_at: string;
}

/** Where the whole-store closed-world check behind the readiness panel stands. */
export type ClosedWorldCheck =
  | { state: 'ready' }
  | { state: 'running'; started_by: string; started_at: string }
  | ({ state: 'waiting' } & AnalysisRun)
  | { state: 'failed'; error: string }
  /** Sent to a guest, who may not start the check. */
  | { state: 'not_computed' };

export function analysisName(analysis: Analysis): string {
  switch (analysis) {
    case 'data_review':
      return 'data review';
    case 'readiness':
      return 'ontology readiness check';
    case 'namespace_usage':
      return 'prefix usage check';
  }
}

export function waitingForMessage(run: AnalysisRun, formatTime: (iso: string) => string): string {
  return `Waiting for the ${analysisName(run.analysis)} to finish — ${run.started_by} started it at ${formatTime(run.started_at)}.`;
}

export function closedWorldCheckPending(check: ClosedWorldCheck | null | undefined): boolean {
  return check?.state === 'running' || check?.state === 'waiting';
}

/** What the readiness panel says about the closed-world check; null once it is ready. */
export function closedWorldCheckMessage(
  check: ClosedWorldCheck,
  formatTime: (iso: string) => string,
): string | null {
  switch (check.state) {
    case 'ready':
      return null;
    case 'running':
      return `Checking every fact against the ontology’s rules — ${check.started_by} started the check at ${formatTime(check.started_at)}. The score and closed-world validity fill in when it finishes.`;
    case 'waiting':
      return `${waitingForMessage(check, formatTime)} The closed-world check runs next; the score and closed-world validity fill in after it.`;
    case 'failed':
      return `The closed-world check did not finish. ${describeApiError(500, check.error)} The score above leaves it out; Refresh to check again.`;
    case 'not_computed':
      return 'The closed-world check has not run on the data as it is now, so the score above leaves it out. It runs when a signed-in person opens this panel.';
  }
}

/** How often the readiness panel asks again: while the index rebuilds, and
 *  while the closed-world check runs or waits its turn. */
export function readinessRefetchMs(
  data:
    | { index?: { state: string } | null; closed_world_check?: ClosedWorldCheck | null }
    | undefined,
): number | false {
  if (data?.index?.state === 'building') return 5_000;
  return closedWorldCheckPending(data?.closed_world_check) ? 10_000 : false;
}
